/**
 * Confirm a restricted delete, naming the record and whatever is blocking it.
 *
 * Asks the server what is holding the record *before* offering the button, so
 * the refusal is information rather than a rejection. A dialog that offers
 * Delete and then says no is a worse version of the same message.
 *
 * The Delete button is absent, not disabled, while anything blocks — a
 * disabled control with the reason beside it invites a click and then refuses
 * it, and the reason is what the person needs anyway.
 *
 * Blockers that can be acted on carry a link. A call has its own delete, so
 * "1 call" takes you to that call; a submission does not, so it is named and
 * counted and left alone. Naming a blocker without saying where it is leaves
 * somebody hunting, which is the whole failure this replaces.
 */
import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, ExternalLink, Loader2, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { client, errorMessage } from '../api/client';

export interface DeleteBlocker {
  entity: string;
  label: string;
  count: number;
  ids: string[];
}

interface DeleteCheck {
  record: string;
  deletable: boolean;
  blockers: DeleteBlocker[];
}

/**
 * A typed confirmation, for deletes whose subject cannot be recovered.
 *
 * The phrase is the record's own identifier — an email address — rather than a
 * word like DELETE. Typing DELETE proves somebody read a dialog; typing the
 * address proves they read *which* record, and the failure worth guarding here
 * is not haste but the wrong row: two entries a list apart, one of them a real
 * person.
 *
 * The server checks it as well. This is the half that stops a misclick; that
 * half is what makes it a rule.
 */
export interface TypedConfirmation {
  phrase: string;
  /** Body key the server compares against, in camelCase as everywhere else. */
  field: string;
  prompt: string;
}

export default function DeleteRecordDialog({
  noun,
  checkUrl,
  deleteUrl,
  linkFor,
  confirm,
  onClose,
  onDeleted,
}: {
  /** "candidate" / "call" — used in the prose, so it reads as a sentence. */
  noun: string;
  checkUrl: string;
  deleteUrl: string;
  /**
   * Where to send somebody to clear a blocker, or null when there is nowhere
   * useful to go. Returning null is the honest answer for a submission —
   * there is no delete for one here.
   */
  linkFor?: (blocker: DeleteBlocker) => { to: string; label: string } | null;
  /** Omitted for deletes where naming the record in the dialog is enough. */
  confirm?: TypedConfirmation;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [failed, setFailed] = useState<string | null>(null);
  const [typed, setTyped] = useState('');

  // Trimmed and case-folded, matching the server. Somebody who pasted an
  // address with a trailing space has still identified the right account, and
  // refusing that teaches them to distrust the check rather than read it.
  const confirmed =
    !confirm || typed.trim().toLowerCase() === confirm.phrase.trim().toLowerCase();

  const check = useQuery<DeleteCheck>({
    queryKey: ['delete-check', checkUrl],
    queryFn: async () => (await client.get(checkUrl)).data,
    // Asked fresh every time the dialog opens: a blocker cleared in another
    // tab must not be remembered as still blocking.
    staleTime: 0,
    retry: false,
  });

  const remove = useMutation({
    mutationFn: async () =>
      (
        await client.delete(deleteUrl, {
          data: confirm ? { [confirm.field]: typed.trim() } : undefined,
        })
      ).data,
    onSuccess: (data: { detail?: string }) => {
      toast.success(data?.detail ?? `${noun} deleted`);
      onDeleted();
    },
    // The server re-checks and can still refuse — something may have been
    // attached between the check and the click. Shown here rather than as a
    // toast, because the dialog is where the decision was made.
    onError: (e) => setFailed(errorMessage(e, `Could not delete this ${noun}`)),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-gray-200 px-5 py-3">
          <h3 className="text-sm font-semibold text-gray-900">
            Delete this {noun}?
          </h3>
          <button type="button" className="btn-ghost px-2 py-1" onClick={onClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="space-y-3 px-5 py-4">
          {check.isLoading ? (
            <div className="flex justify-center py-4 text-gray-400">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : check.isError ? (
            <p className="text-sm text-red-700">
              {errorMessage(check.error, 'Could not check this record.')}
            </p>
          ) : (
            <>
              {/* Named, so nobody deletes "this record" and finds out later
                  which one it was. */}
              <p className="rounded-lg bg-gray-50 px-3 py-2 text-sm font-medium text-gray-900">
                {check.data?.record}
              </p>

              {check.data?.deletable ? (
                <p className="text-xs text-gray-600">
                  Nothing references this {noun}, so deleting it removes only
                  this record. It cannot be undone, and the deletion is
                  recorded.
                </p>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2 rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-amber-200">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                    <p className="text-xs text-amber-900">
                      This {noun} cannot be deleted while anything refers to
                      it. Nothing here will remove those for you — clear them
                      first, and this {noun} can then go on its own.
                    </p>
                  </div>
                  <ul className="space-y-1">
                    {check.data?.blockers.map((blocker) => {
                      const link = linkFor?.(blocker) ?? null;
                      return (
                        <li
                          key={blocker.entity}
                          className="flex items-center justify-between gap-2 rounded border border-gray-200 px-2.5 py-1.5 text-xs"
                        >
                          <span className="text-gray-800">{blocker.label}</span>
                          {link && (
                            <a
                              className="inline-flex shrink-0 items-center gap-1 text-indigo-600 hover:underline"
                              href={link.to}
                            >
                              {link.label}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}

              {confirm && check.data?.deletable && (
                <label className="block text-xs text-gray-600">
                  {confirm.prompt}
                  <input
                    className="input mt-1 text-sm"
                    autoComplete="off"
                    spellCheck={false}
                    value={typed}
                    onChange={(e) => setTyped(e.target.value)}
                    placeholder={confirm.phrase}
                  />
                </label>
              )}

              {failed && (
                <p
                  className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700"
                  role="alert"
                >
                  {failed}
                </p>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-3">
          <button type="button" className="btn-ghost text-sm" onClick={onClose}>
            {check.data?.deletable ? 'Cancel' : 'Close'}
          </button>
          {/* Absent rather than disabled while blocked. */}
          {check.data?.deletable && (
            <button
              type="button"
              className="btn-primary bg-red-600 text-sm hover:bg-red-700"
              // Disabled rather than absent, unlike the blocked case: there
              // the button can never become available and its absence is the
              // message, whereas here it is one field away and the person is
              // mid-way through filling it in.
              disabled={remove.isPending || !confirmed}
              onClick={() => {
                setFailed(null);
                remove.mutate();
              }}
            >
              {remove.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
